import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userIdentityQuery } from '../graphql-operations/UserIdentity.graphql';
import { isBundleCartItem } from '../lib/cart';
import * as types from '../types';
import { userIdentity } from '../types/gql-op-types';

export type FreeTrialRestrictionReason = 'in_cart' | 'previously_purchased' | null;

interface FreeTrialEligibility {
  isEligible: boolean;
  restrictionReason: FreeTrialRestrictionReason;
}

/**
 * Checks if user is eligible for a free trial of the specified collar product type.
 * Returns ineligible if user has a free trial in cart or previously purchased that product type.
 */
export default function useFreeTrialEligibility(collarProductId: string): FreeTrialEligibility {
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);

  const { data: userData } = useQuery<userIdentity>(userIdentityQuery, {
    skip: !collarProductId,
  });

  const previouslyPurchasedCollarProductIds = useMemo(
    () => userData?.currentUser?.previouslyPurchasedCollarProductIds ?? [],
    [userData?.currentUser?.previouslyPurchasedCollarProductIds],
  );

  return useMemo(() => {
    // Check if user has previously purchased this product type
    if (previouslyPurchasedCollarProductIds.includes(collarProductId)) {
      return {
        isEligible: false,
        restrictionReason: 'previously_purchased',
      };
    }

    // Check if cart contains a free trial for this product type
    const cartItems = Object.values(cart.cartItems);
    const hasFreeTrialInCart = cartItems.some((item) => {
      if (!isBundleCartItem(item)) {
        return false;
      }

      const subscriptionProduct = products.find(
        (p): p is types.ISubscriptionProduct =>
          types.isModuleSubscriptionProduct(p) && p.sku === item.subscriptionLineItem.sku,
      );

      return (
        subscriptionProduct?.freeTrial === true &&
        subscriptionProduct.canBePurchasedForProductIds.includes(collarProductId)
      );
    });

    if (hasFreeTrialInCart) {
      return {
        isEligible: false,
        restrictionReason: 'in_cart',
      };
    }

    return {
      isEligible: true,
      restrictionReason: null,
    };
  }, [cart.cartItems, products, previouslyPurchasedCollarProductIds, collarProductId]);
}
